import {
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    Injector,
    Input,
    OnChanges,
    OnInit,
    SimpleChanges,
    ViewChild,
    ViewEncapsulation,
} from '@angular/core';
import { SelectSearchBase } from '@fi-shared/fi-select-search/select-search-base';
import { NG_VALIDATORS, NG_VALUE_ACCESSOR } from '@angular/forms';
import {
    FiDropdownItemAdditionalTextFn,
    FiDropdownItemProfileDataFn,
    FiDropdownItemTitleFn,
    ISelectHierarchyItem,
} from '@fi-shared/fi-select-search/fi-select-search';
import { CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import { isDefined } from '@fi-shared/utils';
import { ObjectTypes } from '@fi-shared/types';
import { fiItemsFlatten } from '@fi-shared/utils/fi-items-flatten';
import { FiProfileSizes, IUserProfile } from '@fi-shared/profile-icon/profile-icon.component';
import { debounce, takeUntil } from 'rxjs/operators';
import { BehaviorSubject, interval } from 'rxjs';
import { FiBrowserEventsService } from '@fi-shared/fi-shared-services/fi-browser-events.service';
import { FiScrollToElementAnimateService } from '@fi-shared/fi-shared-services/scroll-to-element-animate.service';
import { isNullOrUndefined } from '@fi-shared/utils/is-null-or-undefined';

@Component({
    selector: 'fi-foldable-select-item-search',
    templateUrl: './fi-foldable-select-item-search.component.html',
    styleUrl: './fi-foldable-select-item-search.component.scss',
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: FiFoldableSelectItemSearchComponent,
            multi: true,
        },
        {
            provide: NG_VALIDATORS,
            useExisting: FiFoldableSelectItemSearchComponent,
            multi: true,
        },
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: { class: 'fi-foldable-select-item-search' },
    standalone: false,
})
export class FiFoldableSelectItemSearchComponent
    extends SelectSearchBase<ISelectHierarchyItem, number>
    implements OnInit, OnChanges
{
    @Input() items: ISelectHierarchyItem[] = [];
    @Input() label: string;
    @Input() disabled: boolean = false;
    @Input() isCustomMatSelectTrigger: boolean = false;
    @Input() filterBy: string | string[] = 'Name';

    @Input() getOptionTitleFn: FiDropdownItemTitleFn<ISelectHierarchyItem> = () => undefined;
    @Input() getOptionAdditionalTextFn: FiDropdownItemAdditionalTextFn<ISelectHierarchyItem> = () => undefined;
    @Input() getOptionAdditionalClassFn: FiDropdownItemAdditionalTextFn<ISelectHierarchyItem> = () => undefined;
    @Input() trackByFn: (item: ISelectHierarchyItem) => number = (item) => item.Id;
    @Input() getUserProfileFn: FiDropdownItemProfileDataFn<ISelectHierarchyItem> = () => undefined;

    @ViewChild(CdkVirtualScrollViewport) virtualScrollViewportComponent: CdkVirtualScrollViewport;

    readonly maxItemsToShow: number = 9;
    readonly itemSize: number = 50;
    readonly optionItemSize: number = 66;
    private readonly viewPortDefaultMaxSize: number = 400;
    readonly cdkVirtualScrollViewportElId: string = 'cdk-virtual-scroll-viewport-element';
    readonly searchItemSelectedOptionElId: string = 'search-item-selected-option';

    constructor(
        injector: Injector,
        private fiBrowserEventsService: FiBrowserEventsService,
        private elementRef: ElementRef,
        private fiScrollToElementAnimateService: FiScrollToElementAnimateService,
    ) {
        super(injector);
        this.preventMenuItemsViewportHeightUpdate = true;
    }

    expandedItems: ISelectHierarchyItem[] = [];
    private groupItems: ISelectHierarchyItem[] = [];
    private optionItems: ISelectHierarchyItem[] = [];

    ngOnInit() {
        super.ngOnInit();
        this.setGroupItems();
        if (this.ngModel) {
            this.updateSelectedItemsFromModel();
        }
        this.fiBrowserEventsService
            .getWindowEventSubject$('resize')
            .pipe(
                debounce(() => interval(200)),
                takeUntil(this.ngOnDestroy$),
            )
            .subscribe(() => this.setViewportMaxHeight());
    }

    ngOnChanges(changes: SimpleChanges) {
        let change = changes.ngModel;
        if (change && change.currentValue !== change.previousValue) {
            this.updateSelectedItemsFromModel();
        }
        change = changes.items;
        if (change && change.currentValue !== change.previousValue && !change.firstChange) {
            this.setGroupItems();
        }
    }

    getOptionId(itemId: number): string {
        return itemId === this.ngModel ? this.searchItemSelectedOptionElId : '';
    }

    private setGroupItems() {
        const flattenItems: ISelectHierarchyItem[] = fiItemsFlatten(this.items);
        this.groupItems = flattenItems.filter((item: ISelectHierarchyItem) => item.ChildItems?.length);
        this.optionItems = flattenItems.filter((item: ISelectHierarchyItem) => !item.ChildItems?.length);
        this.setViewportHeight();
    }

    protected expandSelectedItemParents() {
        this.expandedItems = [];
        const selectedItem: ISelectHierarchyItem = this.optionItems.find(
            (item: ISelectHierarchyItem) => item.Id === this.ngModel,
        );
        this.expandParentItem(selectedItem);
        this.setViewportHeight();
        this.virtualScrollViewportComponent.checkViewportSize();
        this.scrollToSelectedElement();
    }

    private scrollToSelectedElement() {
        setTimeout(() => {
            const selectedOption: HTMLElement = document.getElementById(this.searchItemSelectedOptionElId);
            const scrollContainer: HTMLElement = document.getElementById(this.cdkVirtualScrollViewportElId);

            if (isNullOrUndefined(selectedOption) || isNullOrUndefined(scrollContainer)) return;

            this.fiScrollToElementAnimateService.scrollToElementWithCompletion({
                targetElement: selectedOption,
                scrollableElement: scrollContainer,
                targetElementOffset: 70,
                maxScrollDurationMS: 500,
            });
        });
    }

    private expandParentItem(item: ISelectHierarchyItem) {
        if (isDefined(item?.ParentId)) {
            const parentItem: ISelectHierarchyItem = this.getItemParent(item);
            this.expandedItems = [...this.expandedItems, parentItem];
            this.expandParentItem(parentItem);
        }
    }

    clearSearch() {
        this.searchText = '';
        this.onSearchTextChanged();
    }

    private readonly defaultGroupHeightPx: number = 48;
    getTopOffset(item: ISelectHierarchyItem): string {
        return (item.Level - 1) * this.defaultGroupHeightPx + 'px';
    }

    getParentWrapperPaddingLeft(item: ISelectHierarchyItem) {
        return item.Level * 16;
    }

    getOptionPaddingLeft(item: ISelectHierarchyItem): number {
        return (item.Level - 1) * 16 - 8;
    }

    getZIndex(item: ISelectHierarchyItem): number {
        return 100 - item.Level;
    }

    isItemExpanded(item: ISelectHierarchyItem): boolean {
        return isDefined(this.getExpandedItem(item));
    }

    isItemBorderShown(parent: ISelectHierarchyItem, item: ISelectHierarchyItem, itemIndex: number): boolean {
        return this.isItemExpanded(parent) && itemIndex === parent.ChildItems?.length - 1 && !this.isItemExpanded(item);
    }

    isOptionDividerShown(parent: ISelectHierarchyItem, index: number): boolean {
        return this.isItemExpanded(parent) && index === parent.ChildItems?.length - 1;
    }

    private getExpandedItem(item: ISelectHierarchyItem) {
        return this.expandedItems.find((expandedItem: ISelectHierarchyItem) => {
            return expandedItem.Id === item.Id && expandedItem.ObjectType === item.ObjectType;
        });
    }

    protected setExpandableItemsOnSearch() {
        if (!this.searchText?.trim().length) {
            this.expandedItems = [];
            return;
        }
        const flattenItems: ISelectHierarchyItem[] = fiItemsFlatten(this.filteredItems$.value);
        const expandedItems: ISelectHierarchyItem[] = flattenItems.filter((item) => this.isItemHighlighted(item));
        this.expandedItems = [...expandedItems];
    }

    private setViewportHeight() {
        const maxItemsHeight: number = this.maxItemsToShow * this.itemSize;
        const flattenItems: ISelectHierarchyItem[] = fiItemsFlatten(this.filteredItems$.value);
        let elementsHeight: number = flattenItems
            .filter((item) => this.isItemShown(item))
            .reduce((total: number, item: ISelectHierarchyItem) => {
                const itemSize: number = this.isOptionSelectHierarchyItem(item) ? this.optionItemSize : this.itemSize;
                return total + itemSize;
            }, 0);
        setTimeout(() => {
            this.viewportHeight$.next(Math.min(maxItemsHeight, elementsHeight, this.viewPortDefaultMaxSize));
            this.cdr.markForCheck();
        });
    }

    private isOptionSelectHierarchyItem(item: ISelectHierarchyItem): boolean {
        return !!this.optionItems.find((option: ISelectHierarchyItem) => {
            return option.Id === item.Id && option.ObjectType === item.ObjectType;
        });
    }

    toggleChildrenVisibility(item: ISelectHierarchyItem) {
        const expandedItem: ISelectHierarchyItem = this.getExpandedItem(item);
        if (!isDefined(expandedItem)) {
            this.expandedItems = [...this.expandedItems, item];
            this.setViewportHeight();
            return;
        }
        const expandedItemIndex: number = this.expandedItems.findIndex((expandedItem: ISelectHierarchyItem) => {
            return expandedItem.Id === item.Id && expandedItem.ObjectType === item.ObjectType;
        });
        this.expandedItems.splice(expandedItemIndex, 1);
        setTimeout(() => this.setViewportHeight());
        this.virtualScrollViewportComponent.checkViewportSize();
    }

    private getItemParent(item: ISelectHierarchyItem): ISelectHierarchyItem {
        return this.groupItems.find((flattenItem: ISelectHierarchyItem) => {
            return flattenItem.Id === item.ParentId && flattenItem.ObjectType === item.ParentObjectType;
        });
    }

    isItemShown(item: ISelectHierarchyItem): boolean {
        if (!isDefined(item.ParentId)) return true;

        const parentItem: ISelectHierarchyItem = this.getItemParent(item);
        return this.isItemExpanded(parentItem) && this.isItemShown(parentItem);
    }

    onSelectOpen(element: HTMLInputElement) {
        this.expandSelectedItemParents();
        // by some reason sometimes we have empty lines in viewport, so should rerender content
        this.virtualScrollViewportComponent.checkViewportSize();
        this.setViewportMaxHeight();
        element.focus();
    }

    viewportMaxHeight$ = new BehaviorSubject<number>(200);
    private readonly viewportExtraOffsetTop: number = 110;
    private readonly viewportExtraOffsetBottom: number = 60;
    private setViewportMaxHeight() {
        const rect: DOMRect = this.elementRef.nativeElement.getBoundingClientRect();
        const documentHeight: number = document.documentElement.scrollHeight;
        const elementOffsetFromTop: number = rect.top + window.scrollY;
        const availableBottomSpace: number = documentHeight - elementOffsetFromTop - this.viewportExtraOffsetTop;
        const maxHeight: number =
            availableBottomSpace >= rect.top ? availableBottomSpace : rect.top - this.viewportExtraOffsetBottom;
        this.viewportMaxHeight$.next(maxHeight);
        this.virtualScrollViewportComponent.checkViewportSize();
    }

    onSelectClose() {
        this.clearSearch();
    }

    getOptionIconClassByObjectType(objectType: ObjectTypes): string {
        if (objectType === ObjectTypes.Area) return 'far fa-earth-americas';
        if (objectType === ObjectTypes.BusinessUnit) return 'fas fa-shop';
    }

    getProfileData(item: ISelectHierarchyItem): IUserProfile {
        return this.getUserProfileFn(item, this.items);
    }

    isProfileIconVisible(objectType: ObjectTypes): boolean {
        return objectType === ObjectTypes.User;
    }

    getOptionTitle(item: ISelectHierarchyItem): string {
        return this.getOptionTitleFn(item, this.items);
    }

    getOptionAdditionalText(item: ISelectHierarchyItem): string {
        return this.getOptionAdditionalTextFn(item, this.items);
    }

    getOptionAdditionalClass(item: ISelectHierarchyItem): string {
        return this.getOptionAdditionalClassFn(item, this.items);
    }

    isItemHighlighted(item: ISelectHierarchyItem) {
        if (!this.searchText?.trim().length) return false;

        if (this.isItemNameIncludesSearchText(item)) return true;

        if (item.ChildItems?.length) return item.ChildItems.some((child) => this.isItemHighlighted(child));
    }

    protected updateFilteredItems() {
        this.filteredItems$.next(this.getFilteredItems());
    }

    private isItemNameIncludesSearchText(item: ISelectHierarchyItem): boolean {
        return item.Name.toLowerCase().includes(this.searchText?.trim().toLowerCase());
    }

    private filterByName(): ISelectHierarchyItem[] {
        if (!this.searchText) return this.items;

        return this.items.reduce<ISelectHierarchyItem[]>(
            (result: ISelectHierarchyItem[], item: ISelectHierarchyItem) => {
                const matches: ISelectHierarchyItem = this.findMatches(item);
                if (matches) {
                    result.push(matches);
                }
                return result;
            },
            [],
        );
    }

    private findMatches(item: ISelectHierarchyItem): ISelectHierarchyItem | null {
        const newItem: ISelectHierarchyItem = Object.assign({}, item);
        newItem.ChildItems = [];

        if (this.isItemNameIncludesSearchText(item)) {
            newItem.ChildItems = [...item.ChildItems];
            return newItem;
        }

        if (item.ChildItems && item.ChildItems.length > 0) {
            const matchingChildren = item.ChildItems.map((child) => this.findMatches(child)).filter(
                (match: ISelectHierarchyItem): match is ISelectHierarchyItem => match !== null,
            );

            if (matchingChildren.length > 0) {
                newItem.ChildItems = matchingChildren;
                return newItem;
            }
        }

        return null;
    }

    private getFilteredItems(): ISelectHierarchyItem[] {
        const filtered: ISelectHierarchyItem[] = this.filterByName();
        setTimeout(() => {
            this.setExpandableItemsOnSearch();
            this.setViewportHeight();
            this.cdr.markForCheck();
        });
        return this.searchText ? filtered : this.items;
    }

    getSelectedItemsFromModel() {
        return !this.ngModel ? [] : [this.optionItems.find((option) => option.Id === this.ngModel)];
    }

    protected readonly fiProfileSizes = FiProfileSizes;
}
